// Keep Pile Styled Components
import styled, { keyframes, css } from 'styled-components';
import { theme } from '../theme';
import { PersonalitySuit } from '../../types/swipe';
import { PERSONALITY_SUITS } from '../../constants/swipe';

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const cardFlyin = keyframes`
  from {
    opacity: 0;
    transform: scale(0.5) translateY(-100px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 10px rgba(102, 126, 234, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.6);
  }
`;

// Keep Pile Header (fixed top bar)
export const KeepPileHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: var(--bg-card);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border-light);
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  z-index: 1000;
  box-shadow: ${theme.shadows.light};
  width: 100%;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
  }
`;

// KeepPileHeaderContent is defined later in the file with full responsive styles

export const KeepPileCounter = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${theme.colors.primary};
    transform: scale(1.05);
  }
`;

export const MiniCardsContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
`;

export const MiniCard = styled.div<{ $suit: PersonalitySuit; $hasNotification?: boolean }>`
  width: 28px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  background: ${props => PERSONALITY_SUITS[props.$suit].colorTheme};
  border: 1.5px solid white;
  box-shadow: ${theme.shadows.light};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: white;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  ${props => props.$hasNotification && css`
    ${css`animation: ${pulseGlow} 2s ease-in-out infinite;`}
  `}
  
  &:hover {
    transform: translateY(-4px) scale(1.1);
    z-index: 10;
    box-shadow: ${theme.shadows.medium};
  }
`;

export const EmptySlot = styled(MiniCard).attrs({ as: 'div' })`
  background: ${theme.colors.background.card};
  border: 2px dashed ${theme.colors.border.medium};
  color: ${theme.colors.text.muted};
  opacity: 0.8;
  box-shadow: ${theme.shadows.light};
  
  &:hover {
    border-color: ${theme.colors.border.dark};
    box-shadow: ${theme.shadows.medium};
  }
`;

// Keep Pile Main Container
export const KeepPileContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  padding: ${theme.spacing.xl} ${theme.spacing.lg};
  padding-bottom: 100px; /* Space for bottom navigation */
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg} ${theme.spacing.md};
    padding-bottom: 120px;
  }
`;

export const KeepPileContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

export const KeepPileTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 ${theme.spacing.sm} 0;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

export const KeepPileSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--text-secondary);
  text-align: center;
  margin: 0 0 ${theme.spacing.xl} 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

// Cards Grid
export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

// Keep Pile Card
export const KeepPileCard = styled.div<{ $priority?: number }>`
  background: var(--bg-card);
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.medium};
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  ${css`animation: ${cardFlyin} 0.5s ease-out;`}
  
  ${props => props.$priority && props.$priority <= 2 && css`
    border: 2px solid ${theme.colors.primary};
    ${css`animation: ${pulseGlow} 2s ease-in-out infinite;`}
  `}
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${theme.shadows.heavy};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    &:hover {
      transform: translateY(-4px);
    }
  }
`;

export const CardImage = styled.div<{ $imageUrl: string }>`
  width: 100%;
  height: 200px;
  background-image: url(${props => props.$imageUrl});
  background-size: cover;
  background-position: center;
  position: relative;
`;

export const CardImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, transparent 100%);
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing.sm};
`;

export const CardBadge = styled.div<{ $variant?: 'priority' | 'expiring' | 'compatibility' }>`
  padding: 4px 12px;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    if (props.$variant === 'priority') return 'rgba(102, 126, 234, 0.9)';
    if (props.$variant === 'expiring') return 'rgba(255, 107, 107, 0.9)';
    return 'rgba(255, 255, 255, 0.9)';
  }};
  color: ${props => props.$variant === 'compatibility' ? '#212529' : 'white'};
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const CardContent = styled.div`
  padding: ${theme.spacing.md};
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.sm};
`;

export const CardName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

export const CardSuitIcon = styled.span<{ $suit: PersonalitySuit }>`
  font-size: 1.5rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
`;

export const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.sm};
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

export const CardInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

export const CardNotes = styled.div`
  background: var(--bg-secondary);
  border-left: 3px solid ${theme.colors.primary};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: ${theme.spacing.sm};
  font-style: italic;
`;

export const CardActions = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.md};
`;

export const CardActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: none;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  
  ${props => {
    if (props.$variant === 'primary') {
      return css`
        background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
        color: white;
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
      `;
    } else if (props.$variant === 'danger') {
      return css`
        background: ${theme.colors.background.secondary};
        color: #FF6B6B;
        border: 1px solid rgba(255, 107, 107, 0.3);
        &:hover {
          background: rgba(255, 107, 107, 0.1);
        }
      `;
    } else {
      return css`
        background: var(--bg-secondary);
        color: var(--text-primary);
        border: 1px solid var(--border-medium);
        &:hover {
          background: #e9ecef;
          border-color: #ced4da;
        }
      `;
    }
  }}
  
  &:active {
    transform: scale(0.98);
  }
`;

// Empty State
export const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} ${theme.spacing.lg};
`;

export const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.7;
  filter: drop-shadow(0 4px 8px rgba(255, 140, 66, 0.3));
`;

export const EmptyStateTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 ${theme.spacing.sm} 0;
`;

export const EmptyStateText = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0 0 ${theme.spacing.lg} 0;
`;

export const EmptyStateButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 140, 66, 0.4);
  }
`;

// Bottom Actions Bar
export const BottomActionsBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-card);
  backdrop-filter: blur(10px);
  border-top: 1px solid var(--border-light);
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  z-index: 90;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    padding-bottom: calc(${theme.spacing.sm} + env(safe-area-inset-bottom));
  }
`;

export const BottomActionsContent = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

export const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  
  ${props => props.$variant === 'primary' ? css`
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
    color: white;
    box-shadow: ${theme.shadows.medium};
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
    }
  ` : css`
    background: #f8f9fa;
    color: #212529;
    border: 1px solid #dee2e6;
    
    &:hover {
      background: #e9ecef;
    }
  `}
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
  }
`;

// Expiration Timer
export const ExpirationTimer = styled.div<{ $isExpiring?: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.$isExpiring ? theme.colors.error : '#6c757d'};
  font-weight: 600;
  margin-top: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

// Sort Controls
export const SortControls = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.xs};
    margin-bottom: ${theme.spacing.md};
  }
`;

export const SortButton = styled.button<{ $isActive: boolean }>`
  background: ${props => props.$isActive 
    ? `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%)`
    : 'var(--bg-secondary)'};
  color: ${props => props.$isActive ? 'white' : 'var(--text-primary)'};
  border: 2px solid ${props => props.$isActive ? theme.colors.primary : 'transparent'};
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  backdrop-filter: blur(0);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.$isActive ? theme.shadows.medium : '0 2px 8px rgba(0, 0, 0, 0.05)'};
    border-color: ${theme.colors.primary};
    background: ${props => props.$isActive 
      ? `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%)`
      : '#e9ecef'};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: 0.8rem;
  }
`;

// Keep Pile Header Specific Styles
export const KeepPileHeaderContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: var(--bg-secondary);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border-light);
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
  }
`;

export const KeepPileHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start; // Changed from space-between
  gap: ${theme.spacing.md}; // Increased from sm for better spacing
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-wrap: wrap;
    gap: ${theme.spacing.sm}; // Keep smaller gap on mobile
  }
`;

export const KeepPileHeaderTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
    width: 100%;
  }
`;

export const KeepPileHeaderCount = styled.span<{ $isFull?: boolean }>`
  font-size: 0.9rem;
  color: ${props => props.$isFull ? theme.colors.error : theme.colors.primary};
  font-weight: 700;
  background: ${props => props.$isFull ? `${theme.colors.error}20` : `${theme.colors.primary}20`};
  padding: 4px 10px;
  border-radius: ${theme.borderRadius.md};
`;

// Group container for cards and button
export const KeepPileCardsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex: 1; // Take available space after count badge
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.xs};
  }
`;

export const KeepPileMiniCards = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  /* Scrolls at EVERY width, not only on mobile. A Premium Plus pile holds 20
     cards; with the row unable to scroll or shrink on desktop it simply grew
     and pushed the View button off the side of the header, so the one control
     that opens the pile was unreachable for exactly the people with the most
     in it. */
  overflow-x: auto;
  min-width: 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const KeepPileHeaderActions = styled.div`
  /* Never squeezed out by the card row beside it. */
  flex-shrink: 0;
  display: flex;
  gap: ${theme.spacing.sm};
`;

export const ViewKeepPileButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xs} ${theme.spacing.sm}; // Horizontal padding for text
  font-size: 0.8rem; // Smaller font for compact design
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap; // Prevent text wrapping
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: auto; // Remove fixed width
  height: auto; // Remove fixed height
  
  &:hover:not(:disabled) {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: 0.75rem; // Slightly smaller on mobile
  }
`;

// Keep Pile Review Modal Components
export const KeepPileOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-overlay);
  backdrop-filter: blur(8px);
  z-index: 10010;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.lg};
  /* The overlay is not a scroll surface; the modal inside it is. Without this
     a drag that starts on the backdrop scrolls the deck behind. */
  overscroll-behavior: none;
  touch-action: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0;
    /* NOT centred on a phone.
       
       A flex item taller than its container overflows equally in both
       directions when centred, and the half that goes upwards is off the top
       of the screen with no way to scroll to it. The modal asks for a full
       viewport height, so any rounding against the real viewport put the
       heading above the top edge. Stretching pins it to the top instead, where
       a heading belongs. */
    align-items: stretch;
  }
`;

export const KeepPileModal = styled.div`
  background: var(--bg-card);
  border-radius: ${theme.borderRadius.xl};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  /* STOPS THE SCROLL LEAKING OUT.
     
     Reaching the top or bottom of this list used to chain the remaining
     momentum to whatever scrollable ancestor sat behind it — on mobile that
     is the swipe deck, so the pile appeared to stop responding while the page
     underneath moved instead. Containment keeps the gesture in this box.
     (No backticks in here: this comment lives inside a template literal.) */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  /* Vertical panning belongs to this list; the overlay above claims the rest,
     which is what keeps a sideways drag from reaching the swipe cards. */
  touch-action: pan-y;
  padding: ${theme.spacing.xl};
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: 0;
    /* dvh, not vh. On a phone 100vh is the LARGE viewport -- the height the
       page would have if the browser's toolbars were hidden -- so a modal
       sized to it is taller than what is actually on screen, every time, by
       the height of the address bar. dvh tracks the viewport that exists.
       The vh pair stays first as the fallback for browsers without dvh. */
    max-height: 100vh;
    height: 100vh;
    max-height: 100dvh;
    height: 100dvh;
    padding: ${theme.spacing.lg} ${theme.spacing.md};
    /* Clears the notch and status bar, which otherwise sit over the heading. */
    padding-top: calc(${theme.spacing.lg} + env(safe-area-inset-top, 0px));
    padding-bottom: calc(${theme.spacing.lg} + env(safe-area-inset-bottom, 0px));
  }
`;

export const ModalHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
  border-bottom: 2px solid var(--border-light);
  padding-bottom: ${theme.spacing.md};
  
  > div {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
  
  h2 {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0;
    color: var(--text-primary);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.spacing.lg};
    
    h2 {
      font-size: 1.5rem;
    }
  }
`;

export const CloseKeepPileReviewLink = styled.button`
  background: none;
  border: none;
  font-size: 0.875rem;
  color: var(--text-primary);
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 4px;
  transition: all 0.2s ease;
  align-self: flex-start;
  font-weight: 500;
  position: relative;
  z-index: 10011;
  
  &:hover {
    color: ${theme.colors.accent};
    text-decoration-thickness: 2px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
`;

export const KeepPileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

export const KeptCard = styled.div<{ $suitColor: string }>`
  background: var(--bg-card);
  border: 2px solid ${props => props.$suitColor};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  transition: all 0.2s ease;
  backdrop-filter: blur(0);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px ${props => props.$suitColor};
    border-color: ${props => props.$suitColor};
  }
`;

export const KeptCardImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 180px;
  }
`;

export const KeptCardName = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin: ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.xs} ${theme.spacing.md};
  color: var(--text-primary);
`;

export const KeptCardCompatibility = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.primary};
  font-weight: 600;
  margin: 0 ${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.md};
`;

export const KeptCardActions = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.md};
`;

export const KeptCardButton = styled.button<{ $variant: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.$variant === 'primary' && `
    background: ${theme.colors.primary};
    color: white;
    &:hover {
      background: ${theme.colors.secondary};
    }
  `}
  
  ${props => props.$variant === 'secondary' && `
    background: #f8f9fa;
    color: #212529;
    border: 1px solid #dee2e6;
    &:hover {
      background: #e9ecef;
    }
  `}
  
  ${props => props.$variant === 'danger' && `
    background: ${theme.colors.error};
    color: white;
    &:hover {
      background: #ff4444;
    }
  `}
`;

export const KeepPileFooter = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  padding-top: ${theme.spacing.xl};
  border-top: 2px solid ${theme.colors.border};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

export const ContinueSwipeButton = styled.button`
  flex: 1;
  background: #f1f3f5;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(0);
  
  &:hover {
    background: #e9ecef;
    border-color: #ced4da;
    color: ${theme.colors.text.primary};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    font-size: 1rem;
  }
`;

export const GoToMatchingButton = styled.button`
  flex: 1;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${theme.shadows.medium};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.heavy};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    font-size: 1rem;
  }
`;

// EmptyStateTitle and EmptyStateMessage are already defined earlier in the file


// "+7" when the pile holds more cards than the header can sensibly show.
// The row scrolls, but a strip of twenty identical slots is noise rather than
// information — the count badge already says how full the pile is.
export const MiniCardOverflow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 36px;
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.background.secondary};
  border: 1px dashed ${theme.colors.border.medium};
  color: ${theme.colors.text.secondary};
  font-size: 0.7rem;
  font-weight: 700;

  @media (max-width: 480px) {
    width: 24px;
    height: 32px;
  }
`;
